import React, { useState, useRef, useMemo } from 'react';
import { Player } from '@lordicon/react';
import {
  useAppContext,
  useTranslator,
  useToast,
  callNativeMobileApp,
  formatCalories,
} from '@front-activerse/common-components';
import { Button, Modal, ModalBody, ModalFooter, Row, Col } from 'reactstrap';
import { useNavigate } from 'react-router-dom';

interface ITrackerActivityComponentProps {
  id: number;
  activityName: string;
  activityTypeName: string;
  caloriesBurned: number;
  durationSeconds: number;
  distance: number;
  distanceUnit: string;
  imageUrl: string;
  loggedAt: string;
  activitySourceType: string;
  removeActivity: (activityId: number) => Promise<any>;
  date: string;
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const remainingSeconds = seconds % 3600;
  const minutes = Math.floor(remainingSeconds / 60);
  const remainingSeconds2 = remainingSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds2.toString().padStart(2, '0')}`;
};

const formatTimeFromDate = (dateString: string): string => {
  const match = dateString.match(/T(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : '';
};

const TrackerActivityComponent: React.FC<ITrackerActivityComponentProps> = ({
  id,
  activityName,
  activityTypeName,
  distance,
  distanceUnit,
  caloriesBurned,
  durationSeconds,
  imageUrl,
  loggedAt,
  activitySourceType,
  removeActivity,
  date,
}) => {
  const navigate = useNavigate();
  const playerRefEdit = useRef<Player>(null);
  const playerRefRemove = useRef<Player>(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const toggleModal = () => {
    setShowAlertModal(!showAlertModal);
  };
  const { t } = useTranslator();
  const {
    images: {
      trackerLoggedActivityThumb,
      trackerSyncedActivityAppleThumb,
      trackerSyncedActivityGoogleThumb,
      aiBadgeTiny,
    },
    lotties: { trashIcon, editIcon },
  } = useAppContext();

  const { toastError } = useToast();

  const iconsIconMiddleColor = useMemo(() => {
    if (typeof document === 'undefined') return '#000000';
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--icons-icon-middle-color')
      .trim();

    return color || '#000000';
  }, []);

  const renderSourceType = () => {
    if (activitySourceType === 'video_workout') {
      return t('tracker.history.entryType.workout');
    } else if (activitySourceType === 'manual') {
      return t('tracker.history.entryType.manual');
    } else if (activitySourceType === 'apple_health') {
      return t('tracker.history.entryType.synced');
    } else if (activitySourceType === 'google_fit') {
      return t('tracker.history.entryType.synced');
    } else if (activitySourceType === 'ai') {
      return (
        <>
          <img
            src={aiBadgeTiny}
            alt=""
            className="me-1"
            style={{ width: '26px', marginTop: '-2px' }}
          />
          {t('tracker.history.entryType.manualActivity')}
        </>
      );
    } else {
      return t('tracker.history.entryType.workout');
    }
  };

  const removeActivityHandler = async () => {
    setShowAlertModal(false);
    const response = await removeActivity(id);
    if (response.code === 200) {
      // refresh native tracker data
      callNativeMobileApp({
        action: 'refresh_data',
        payload: {
          module: 'tracker',
          date: date,
        },
      });
    } else {
      toastError();
    }
  };

  const renderImage = () => {
    let image = null;

    if (imageUrl) {
      image = imageUrl;
    } else {
      if (activitySourceType === 'video_workout') {
        image = trackerLoggedActivityThumb;
      } else if (activitySourceType === 'apple_health') {
        image = trackerSyncedActivityAppleThumb;
      } else if (activitySourceType === 'google_fit') {
        image = trackerSyncedActivityGoogleThumb;
      } else {
        image = trackerLoggedActivityThumb;
      }
    }

    return image;
  };

  const isClickable = activitySourceType === 'ai';

  return (
    <>
      <div className={`tracker-history-item d-flex ${isClickable ? 'cursor-pointer' : ''}`}>
        <img src={renderImage()} className="tracker-history-item-icon" alt="" />

        <div
          className="flex-grow-1 text-start"
          onClick={
            isClickable ? () => navigate(`/tracker/${date}/activity-details/${id}`) : undefined
          }
        >
          <div className="tracker-history-item-meta mb-xxs mt-s">
            <p className="description text-text-variant-color mb-0">
              <span>{formatTimeFromDate(loggedAt)}</span>
              <strong className="bullet-separator mx-1">•</strong>
              <span>{renderSourceType()}</span>
            </p>
          </div>
          <p className="small mb-xxs">{activityName ? activityName : activityTypeName}</p>
          <div className="d-flex justify-content-between">
            <p className="description text-text-main-color mb-s">
              <span role="img" aria-label="weightlifting">
                🏋️‍♀️
              </span>{' '}
              {formatCalories(caloriesBurned)} kcal
            </p>
            <p className="description text-text-variant-color mb-s text-end">
              {t('tracker.history.time.title')}{' '}
              <span className="text-text-main-color">{formatDuration(durationSeconds)}</span>
              <br />
              {distance && (
                <>
                  {t('tracker.history.distance.title')}{' '}
                  <span className="text-text-main-color">
                    {distance} {distanceUnit}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>

        <div className="tracker-history-item-actions">
          {activitySourceType === 'ai' && (
            <Button
              className="p-0 me-m"
              color="link"
              onClick={() => navigate(`/tracker/${date}/activity-details/${id}`)}
              onMouseEnter={() => playerRefEdit.current?.playFromBeginning()}
              onTouchStart={() => playerRefEdit.current?.playFromBeginning()}
            >
              <Player
                ref={playerRefEdit}
                icon={editIcon}
                size={20}
                colorize={iconsIconMiddleColor}
              />
            </Button>
          )}
          <Button
            className="p-0"
            color="link"
            onClick={toggleModal}
            onMouseEnter={() => playerRefRemove.current?.playFromBeginning()}
            onTouchStart={() => playerRefRemove.current?.playFromBeginning()}
          >
            <Player
              ref={playerRefRemove}
              icon={trashIcon}
              size={20}
              colorize={iconsIconMiddleColor}
            />
          </Button>
        </div>
      </div>

      <Modal isOpen={showAlertModal} toggle={toggleModal} centered size="md">
        <ModalBody>
          <h4 className="title-medium semi-bold mb-l mt-s">
            {activitySourceType === 'video_workout' || activitySourceType === 'manual'
              ? t('tracker.history.removePopup.appWorkout.title')
              : t('tracker.history.removePopup.syncedActivity.title')}
          </h4>

          <p className="mb-0 text-text-variant-color">
            {activitySourceType === 'video_workout'
              ? t('tracker.history.removePopup.appWorkout.description')
              : t('tracker.history.removePopup.syncedActivity.description')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Row className="w-100 m-0">
            <Col xs="6" className="ps-0 pe-2">
              <Button color="secondary" outline className="w-100" onClick={() => toggleModal()}>
                {t('tracker.history.removePopup.cancelButton')}
              </Button>
            </Col>
            <Col xs="6" className="ps-2 pe-0">
              <Button
                color="primary"
                className="w-100"
                onClick={() => removeActivityHandler()}
                disabled={!showAlertModal}
              >
                {t('tracker.history.removePopup.removeButton')}
              </Button>
            </Col>
          </Row>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TrackerActivityComponent;
